'use client';

import React from 'react';
import { Demo, DemoType } from '@/components/frontend/object-audio/demo';
import { LearnBlock, learnBlocks } from '@/components/frontend/object-audio/learn-block';
import { HeaderImage } from '@/components/frontend/object-audio/marketing';
import { AudioProvider } from '@/components/frontend/object-audio/object-audio-context';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MessageWithIcon } from '@/components/ui/message';
import { H1, H3, P } from '@/components/ui/typography';
import { demoOneLevels, demoTwoLevels } from '@/lib/frontend/utils/oa-demo';
import { Info, Music4, SlidersVertical } from 'lucide-react';
import Link from 'next/link';

import voxelLayoutImage from '@/assets/images/object-audio/voxel-layout.jpg';

const LevelIcon = () => (
  <span className="rounded-lg bg-accent-three text-background inline-flex items-center justify-center w-6 h-6">
    <SlidersVertical className="w-4 h-4" />
  </span>
);

const ObjectIcon = () => (
  <span className="rounded-full bg-accent text-background inline-flex items-center justify-center w-6 h-6">
    <Music4 className="w-4 h-4" />
  </span>
);

const DemoPage = () => {
  return (
    <AudioProvider>
      <div className="max-w-[1000px] m-auto flex flex-col gap-10 mb-10 px-5">
        <div className="flex flex-col gap-5">
          <HeaderImage />
          <div className="flex items-center gap-2">
            <H1>Object Audio</H1>
            <Badge className="font-bold text-sm">New in v5.5</Badge>
          </div>
          <div>
            <Link href="/docs/v5/audio/object-audio" className="underline">
              Read the documentation
            </Link>
          </div>
          <P>
            Object audio, or object-based mixing, is a way of working with audio routing that is
            more flexible and more visual than traditional channel-based mixing.
          </P>
          {/* 
          <P>
            In this very simple example of traditional channel-based mixing,
            etc. etc.{' '}
            <span className="bg-yellow-200 text-black">
              (is something like this worth having)?
            </span>
          </P>
          <div className="flex justify-center">
            <TraditionalDemo />
          </div> */}
          <P>
            Object mixers present a graphical interface that can be controlled spatially by the
            person doing the mixing. Inputs are represented by objects which can be moved around the
            mixer freely. The spatial position of an object within the object mixer determines how
            its sound is routed.
          </P>

          <H3>Simplified Demo</H3>
          <P>
            Here we have two sets of audio levels, A and B, each of which has an output channel for
            the left and right speaker. In the map, levels A and B are represented by the two{' '}
            <LevelIcon />
            &nbsp; icons. The&nbsp;
            <ObjectIcon />
            &nbsp; icon is an object, which has an audio file assigned to it. The output routing of
            the sound from the object changes based on the location of the &nbsp;
            <ObjectIcon />
            &nbsp; icon relative to the <LevelIcon /> icons.
          </P>
          <P>
            Hit play and try changing the levels and moving the object around the map and see how
            the resulting mix changes.
          </P>
          <MessageWithIcon
            icon={<Info className="w-4 h-4" />}
            title="Note"
            description={`This demo is most effective with the sound on and either stereo
              headphones or speakers.`}
          />
          <div className="flex justify-center my-10">
            <Demo initialLevels={demoOneLevels} src="/audio/lowkeyloop.mp3" />
          </div>
          <P>
            You'll notice that when the object is placed on top of Levels A, the routing of the
            object’s audio matches the routing set in Levels A. As you move the object away from
            Levels A and towards Levels B, the object’s routing changes. The closer it gets to
            Levels B, the more influence Levels B has on the object’s routing.
          </P>

          <H3>Mapping a Physical Space</H3>
          <P>
            Graphically mixing the audio in this way is can be useful on its own, but another
            powerful way to think about the map above is as a representation of an actual, physical
            space.
          </P>

          <div className="flex justify-center my-10">
            <Demo
              initialLevels={demoTwoLevels}
              backgroundImage={voxelLayoutImage}
              aspectRatio="1.65742 / 1"
              maxWidth="100%"
              src="/audio/birds-and-bells.mp3"
            />
          </div>

          <P>
            Let's imagine that this theater has two main speakers called L and R. If we place Levels
            A on the map where the L speaker is placed in the theater, and we route Levels A to only
            the L output, then repeat the process with Levels B and the R speaker, then map has
            effectively become a map of the theater. When the object is near Levels A, we hear it
            playing from the L speaker, and when it's near Levels B, we hear it playing from the R
            speaker.
          </P>

          <H3>Moving the Object</H3>
          <P>
            Static settings are great, but what if the object moved around the stage, modulating the
            sound we hear from the speakers? E.g. to emulate a car moving from left to right, or
            perhaps a helicopter circling around the stage.
          </P>

          <Demo
            initialLevels={demoTwoLevels}
            backgroundImage={voxelLayoutImage}
            type={DemoType.Path}
            aspectRatio="1.65742 / 1"
            maxWidth="100%"
            src="/audio/helicopter.mp3"
          />

          <H3>Object audio in QLab</H3>
          <P>
            This demo only scratches the surface of what object audio can do. QLab supports up to
            128 outputs, multiple objects per cue, custom path drawing for object movements, and
            much more, allowing for limitless possibilities and flexibility for your show.
          </P>

          <div className="flex flex-col gap-10">
            <div className="flex gap-5 sm:flex-row flex-col">
              {[learnBlocks[1], learnBlocks[2], learnBlocks[3]].map((b) => (
                <LearnBlock key={b.title} {...b} />
              ))}
            </div>
          </div>

          <div className="flex justify-center mt-10">
            <Link href="/download">
              <Button>Download QLab 5</Button>
            </Link>
          </div>
        </div>
      </div>
    </AudioProvider>
  );
};

export default DemoPage;
